import {addDays, differenceInCalendarDays, isBefore, isValid, max} from 'date-fns'

import {jobLength} from 'src/jobs/job-entry/job-entry-logic'
import {parseApiDate, toApiDateString} from 'src/jobs/job-schedule-format'
import {requirementsAsBilled} from 'src/jobs/order-billing'
import type {JobDetail} from 'src/jobs/schemas'
import {isRealSite, outOfStateLabel} from 'src/lib/texas-bounds'
import type {CreateProjectJobInput} from 'src/projects/schemas'

// Maximum jobs per create request.
export const MAX_SITES = 100

export type LineColumn = {
    key: `line:${number}`
    kind: 'worker' | 'equipment'
    typeId: string
    label: string
    defaultQuantity: number
}

export type SiteColumn =
    | {key: 'site'; label: 'Site name'}
    | {key: 'place'; label: 'Address or lat, long'}
    | LineColumn
    | {key: 'date'; label: 'Date'}

export type SiteRow = Record<string, string>

export type Place = {latitude: number; longitude: number; fullAddress: string | null}

export function siteColumns(job: Pick<JobDetail, 'workerRequirements' | 'assetRequirements'>) {
    const lines: LineColumn[] = [
        ...job.workerRequirements.map((requirement) => ({
            kind: 'worker' as const,
            typeId: requirement.workerTypeId,
            label: requirement.workerTypeName,
            defaultQuantity: requirement.quantity,
        })),
        ...job.assetRequirements.map((requirement) => ({
            kind: 'equipment' as const,
            typeId: requirement.rentalAssetTypeId,
            label: requirement.rentalAssetTypeName,
            defaultQuantity: requirement.quantity,
        })),
    ].map((line, index) => ({...line, key: `line:${index}` as const}))
    return {
        columns: [
            {key: 'site', label: 'Site name'},
            {key: 'place', label: 'Address or lat, long'},
            ...lines,
            {key: 'date', label: 'Date'},
        ] as SiteColumn[],
        lines,
    }
}

export function emptyRow(): SiteRow {
    return {}
}

export function isBlankRow(row: SiteRow): boolean {
    return Object.values(row).every((cell) => cell.trim() === '')
}

// Split delimited text into rows of cells. Double-quoted cells may contain separators and newlines.
function splitDelimitedTable(text: string, separator: ',' | '\t'): string[][] {
    const rows: string[][] = []
    let cells: string[] = []
    let cell = ''
    let quoted = false
    const endRow = () => {
        cells.push(cell)
        rows.push(cells)
        cells = []
        cell = ''
    }
    for (let index = 0; index < text.length; index++) {
        const character = text[index]!
        if (quoted) {
            if (character === '"' && text[index + 1] === '"') {
                cell += '"'
                index++
            } else if (character === '"') {
                quoted = false
            } else {
                cell += character
            }
        } else if (character === '"' && cell.trim() === '') {
            quoted = true
            cell = ''
        } else if (character === separator) {
            cells.push(cell)
            cell = ''
        } else if (character === '\n') {
            endRow()
        } else {
            cell += character
        }
    }
    endRow()
    return rows
}

const normalizeHeaderText = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, '')

const HEADER_ALIASES: Record<string, string[]> = {
    site: ['site', 'sitename', 'name', 'sites', 'location', 'locationname', 'pad', 'well'],
    place: ['address', 'addressorlatlong', 'latlong', 'latlng', 'coordinates', 'gps', 'place'],
    lat: ['lat', 'latitude'],
    long: ['long', 'lng', 'lon', 'longitude'],
    date: ['date', 'day', 'startdate', 'deliverydate'],
}

function matchHeaderColumnKey(cell: string, lines: LineColumn[]): string | null {
    const name = normalizeHeaderText(cell)
    if (!name) {
        return null
    }

    for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
        if (aliases.includes(name)) {
            return key
        }
    }
    return lines.find((line) => normalizeHeaderText(line.label) === name)?.key ?? null
}

const isNumericCell = (cell: string | undefined) => cell != null && /^-?\d+(\.\d+)?$/.test(cell)

function looksLikeDataCell(cell: string): boolean {
    return (
        /\d/.test(cell) &&
        (isNumericCell(cell) || parseCoordinates(cell) != null || /\d\/\d/.test(cell))
    )
}

function joinSplitCoordinates(cells: string[], placeIndex: number): string[] {
    const latitude = cells[placeIndex]
    const longitude = cells[placeIndex + 1]
    return isNumericCell(latitude) && isNumericCell(longitude)
        ? [
              ...cells.slice(0, placeIndex),
              `${latitude}, ${longitude}`,
              ...cells.slice(placeIndex + 2),
          ]
        : cells
}

/** A first row naming the columns maps them by name; otherwise cells go in column order. */
export function parsePastedRows(text: string, columns: SiteColumn[]): SiteRow[] {
    const lines = columns.filter((column): column is LineColumn => column.key.startsWith('line:'))
    const tabbed = text.includes('\t')
    const table = splitDelimitedTable(text.replace(/\r\n?/g, '\n'), tabbed ? '\t' : ',')
        .map((cells) => cells.map((cell) => cell.trim()))
        .filter((cells) => cells.some((cell) => cell !== ''))
    if (table.length === 0) {
        return []
    }

    const firstRow = table[0]!
    const headerKeys = firstRow.map((cell) => matchHeaderColumnKey(cell, lines))
    const named = headerKeys.filter((key) => key != null).length
    const hasHeader =
        named > 0 &&
        named >= firstRow.filter((cell) => cell !== '').length / 2 &&
        !firstRow.some(looksLikeDataCell)
    if (hasHeader) {
        const placeIndex = headerKeys.indexOf('place')
        return table.slice(1).map((typed) => {
            const cells =
                !tabbed && placeIndex >= 0 && typed.length > headerKeys.length
                    ? joinSplitCoordinates(typed, placeIndex)
                    : typed
            const row: SiteRow = {}
            headerKeys.forEach((key, index) => {
                if (key && key !== 'lat' && key !== 'long') {
                    row[key] = cells[index] ?? ''
                }
            })
            const lat = headerKeys.indexOf('lat')
            const long = headerKeys.indexOf('long')
            if (lat >= 0 && long >= 0 && (cells[lat] || cells[long])) {
                row['place'] = `${cells[lat] ?? ''}, ${cells[long] ?? ''}`
            }
            return row
        })
    }
    const placeIndex = columns.findIndex((column) => column.key === 'place')
    return table.map((typed) => {
        const cells = !tabbed && placeIndex >= 0 ? joinSplitCoordinates(typed, placeIndex) : typed
        const row: SiteRow = {}
        columns.forEach((column, index) => {
            if (index < cells.length) {
                row[column.key] = cells[index]!
            }
        })
        return row
    })
}

export function pasteIntoRows(
    rows: SiteRow[],
    text: string,
    columns: SiteColumn[],
    rowIndex: number,
    columnKey: string,
): {rows: SiteRow[]; pasted: number; cut: boolean} {
    const startColumn = Math.max(
        0,
        columns.findIndex((column) => column.key === columnKey),
    )
    const pasted = parsePastedRows(text, columns.slice(startColumn))
    const next = [...rows]
    pasted.forEach((row, offset) => {
        next[rowIndex + offset] = {...next[rowIndex + offset], ...row}
    })
    if (!isBlankRow(next[next.length - 1] ?? {})) {
        next.push(emptyRow())
    }
    return {
        rows: next.slice(0, MAX_SITES),
        pasted: pasted.length,
        cut: rowIndex + pasted.length > MAX_SITES,
    }
}

export function parseCoordinates(text: string): {latitude: number; longitude: number} | null {
    const match = /^\s*\(?\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*\)?\s*$/.exec(text)
    if (match) {
        return {latitude: Number(match[1]), longitude: Number(match[2])}
    }

    const european = /^\s*\(?\s*(-?\d+,\d+)\s*[;\s]\s*(-?\d+,\d+)\s*\)?\s*$/.exec(text)
    return european
        ? {
              latitude: Number(european[1]!.replace(',', '.')),
              longitude: Number(european[2]!.replace(',', '.')),
          }
        : null
}

function looksLikeBrokenCoordinates(text: string): boolean {
    return /^[\s\d.,;()-]+$/.test(text) && /\d/.test(text)
}

function getCoordinatesProblem(latitude: number, longitude: number): string | null {
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
        return 'Coordinates are out of range. Check for a missing decimal point'
    }
    if (longitude > 0 && isRealSite(latitude, -longitude)) {
        return 'Longitude must be negative'
    }
    if (!isRealSite(latitude, longitude)) {
        return 'Coordinates are invalid. Check for a missing decimal point or minus sign'
    }

    return null
}

const MONTH_NAMES = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
]

function monthFromName(name: string): number | null {
    const word = name.toLowerCase()
    const index = word.length >= 3 ? MONTH_NAMES.findIndex((month) => month.startsWith(word)) : -1
    return index === -1 ? null : index + 1
}

/** Month, day and year text of "2026-10-19", "10/19/2026", "Oct 19, 2026" or "19-Oct-2026". */
function rowDateParts(value: string): [number | null, number, string | undefined] | null {
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value)
    if (iso) {
        return [Number(iso[2]), Number(iso[3]), iso[1]]
    }

    const us = /^(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2}|\d{4}))?$/.exec(value)
    if (us) {
        return [Number(us[1]), Number(us[2]), us[3]]
    }

    const monthFirst = /^([a-z]+)\.?\s+(\d{1,2}),?(?:\s+(\d{2}|\d{4}))?$/i.exec(value)
    if (monthFirst) {
        return [monthFromName(monthFirst[1]!), Number(monthFirst[2]), monthFirst[3]]
    }

    const dayFirst = /^(\d{1,2})[\s-]([a-z]+)\.?(?:[\s-](\d{2}|\d{4}))?$/i.exec(value)
    if (dayFirst) {
        return [monthFromName(dayFirst[2]!), Number(dayFirst[1]), dayFirst[3]]
    }

    return null
}

// Dates without a year resolve to the year closest to today.
export function parseRowDate(text: string, today: Date): Date | null {
    const [month, date, yearText] = rowDateParts(text.trim()) ?? [null, 0, undefined]
    if (month == null) {
        return null
    }

    const year = yearText
        ? Number(yearText.length === 2 ? `20${yearText}` : yearText)
        : getNearestYearForDay(month, date, today)
    const day = new Date(year, month - 1, date)
    // Reject overflowed dates such as Feb 30.
    return isValid(day) && day.getMonth() === month - 1 && day.getDate() === date ? day : null
}

function getNearestYearForDay(month: number, date: number, today: Date): number {
    const distance = (year: number) =>
        Math.abs(differenceInCalendarDays(new Date(year, month - 1, date), today))
    const thisYear = today.getFullYear()
    return [thisYear - 1, thisYear + 1].reduce(
        (best, year) => (distance(year) < distance(best) ? year : best),
        thisYear,
    )
}

export function rowQuantity(row: SiteRow, line: LineColumn): number | null {
    const cell = (row[line.key] ?? '').trim()
    if (cell === '') {
        return line.defaultQuantity
    }

    return /^\d+$/.test(cell) ? Number(cell) : null
}

export type RowErrors = Record<number, Record<string, string>>

export function validateSiteRows(
    rows: SiteRow[],
    lines: LineColumn[],
    today: Date,
): {errors: RowErrors; filled: number[]; tooMany: boolean} {
    const errors: RowErrors = {}
    const filled = rows.flatMap((row, index) => (isBlankRow(row) ? [] : [index]))
    const add = (index: number, key: string, message: string) => {
        errors[index] = {...errors[index], [key]: message}
    }
    for (const index of filled) {
        const row = rows[index]!
        if (!(row['site'] ?? '').trim()) {
            add(index, 'site', 'Site name is required')
        }
        const place = (row['place'] ?? '').trim()
        const coordinates = parseCoordinates(place)
        if (!place) {
            add(index, 'place', 'Address or coordinates are required')
        } else if (coordinates) {
            const problem = getCoordinatesProblem(coordinates.latitude, coordinates.longitude)
            if (problem) {
                add(index, 'place', problem)
            }
        } else if (looksLikeBrokenCoordinates(place)) {
            add(index, 'place', 'Coordinates must be lat, long')
        }
        const quantities = lines.map((line) => rowQuantity(row, line))
        lines.forEach((line, lineIndex) => {
            if (quantities[lineIndex] == null) {
                add(index, line.key, 'Must be a whole number')
            }
        })
        if (lines.length > 0 && quantities.every((quantity) => quantity === 0)) {
            add(index, lines[0]!.key, 'At least one quantity must be above 0')
        }
        const date = (row['date'] ?? '').trim()
        if (date && !parseRowDate(date, today)) {
            add(index, 'date', 'Invalid date. Use Oct 20, 2026 or 10/20/2026')
        }
    }
    return {errors, filled, tooMany: filled.length > MAX_SITES}
}

export function cellProblem(
    row: SiteRow,
    key: string,
    lines: LineColumn[],
    today: Date,
): string | null {
    return validateSiteRows([row], lines, today).errors[0]?.[key] ?? null
}

export function placeOutOfState(place: Place): string | null {
    return outOfStateLabel({
        latitude: place.latitude,
        longitude: place.longitude,
        addressText: place.fullAddress,
    })
}

export function placeFromRow(row: SiteRow): Place | null {
    const coordinates = parseCoordinates(row['place'] ?? '')
    return coordinates ? {...coordinates, fullAddress: null} : null
}

export function typedOutOfState(row: SiteRow): string | null {
    const place = placeFromRow(row)
    return place ? placeOutOfState(place) : null
}

function parseTypedRowDate(row: SiteRow, today: Date): Date | null {
    return (row['date'] ?? '').trim() ? parseRowDate(row['date']!, today) : null
}

export function rowDays(
    row: SiteRow,
    job: Pick<JobDetail, 'startDate' | 'endDate'>,
    today: Date,
): {start: Date; end: Date} {
    const start = parseTypedRowDate(row, today) ?? max([parseApiDate(job.startDate), today])
    return {start, end: addDays(start, jobLength(job) - 1)}
}

export function rowInPast(
    row: SiteRow,
    job: Pick<JobDetail, 'startDate' | 'endDate'>,
    today: Date,
): boolean {
    return isBefore(rowDays(row, job, today).start, today)
}

/** Strip the trailing first-location suffix from the job name. */
export function baseJobName(job: Pick<JobDetail, 'name' | 'locations'>): string {
    const site = job.locations[0]?.name?.trim()
    const suffix = site ? ` – ${site}` : null
    return suffix && job.name.endsWith(suffix) && job.name.length > suffix.length
        ? job.name.slice(0, -suffix.length)
        : job.name
}

/** Build a job input from the source job and row values. Zero-quantity lines are omitted. */
export function toSiteJobInput(
    row: SiteRow,
    place: Place,
    job: JobDetail,
    lines: LineColumn[],
    today: Date,
): CreateProjectJobInput {
    const {start, end} = rowDays(row, job, today)
    const plannedEnd = job.plannedEndDate ? parseApiDate(job.plannedEndDate) : null
    const quantityAt = (index: number) => {
        const line = lines[index]
        return line ? (rowQuantity(row, line) ?? 0) : 0
    }
    const crewCount = job.workerRequirements.length
    return {
        status: job.status === 'planned' || job.status === 'cancelled' ? 'planned' : 'active',
        name: `${baseJobName(job)} – ${row['site']!.trim()}`,
        serviceTypeId: job.serviceTypeId,
        specificType: job.specificType,
        notes: job.notes,
        customerContactId:
            job.customerContactSource === 'job' ? (job.customerContact?.id ?? null) : null,
        poNumber: job.poNumber,
        ...(job.projectBillingTermsId ? {projectBillingTermsId: job.projectBillingTermsId} : {}),
        startDate: toApiDateString(start),
        startTime: job.startTime,
        endDate: toApiDateString(end),
        endTime: null,
        ...(job.bookByDay ? {bookByDay: true} : {}),
        plannedEndDate:
            plannedEnd && differenceInCalendarDays(plannedEnd, start) >= 0
                ? toApiDateString(plannedEnd)
                : null,
        locations: [
            {
                name: row['site']!.trim(),
                latitude: place.latitude,
                longitude: place.longitude,
                notes: null,
                fullAddress: place.fullAddress,
            },
        ],
        workerRequirements: requirementsAsBilled(
            job.billingMethod,
            job.workerRequirements,
            () => false,
        ).flatMap((requirement, index) => {
            const quantity = quantityAt(index)
            return quantity > 0
                ? [
                      {
                          workerTypeId: requirement.workerTypeId,
                          quantity,
                          billingRate: requirement.billingRate,
                          billingType: requirement.billingType,
                          estimatedHours: requirement.estimatedHours,
                      },
                  ]
                : []
        }),
        assetRequirements: requirementsAsBilled(
            job.billingMethod,
            job.assetRequirements,
            () => true,
        ).flatMap((requirement, index) => {
            const quantity = quantityAt(crewCount + index)
            return quantity > 0
                ? [
                      {
                          rentalAssetTypeId: requirement.rentalAssetTypeId,
                          quantity,
                          billingRate: requirement.billingRate,
                          billingType: requirement.billingType,
                      },
                  ]
                : []
        }),
    }
}

export function describeCounts(row: SiteRow, lines: LineColumn[]): string {
    const counts = lines.flatMap((line) => {
        const quantity = rowQuantity(row, line) ?? 0
        return quantity > 0 ? [`${quantity} ${line.label}`] : []
    })
    return counts.join(', ')
}
